import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { removeArcgisLayerData } from '@atom/actions/arcgisLayerActions';
import DeleteModal from '@atom/components/common/DeleteModal';
import { Checkbox, Icon, IconButton, Progress, Snackbar } from '@atom/mui';
import {
  DeleteIcon,
  EditIcon,
  InfoOutlinedIcon,
  RefreshIcon,
} from '@atom/mui/icons';
import type { ArcgisLayer } from '@atom/types/arcgis';
import type { RootState } from '@atom/types/store';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { toggleFromSet } from '@atom/utilities/setUtilities';

import ArcgisLayerIcon from '../arcgis/ArcgisLayerIcon';
import { useArcgisLayers } from '../arcgis/ArcgisLayersProvider';
import { useMapLayers } from '../MapPortalDataProvider';

import GeospatialLayerModal from './GeospatialLayerModal';

import classes from './filterPanel.module.css';

const formatLastSynced = (lastSynced: number): string =>
  `Last synced ${format(new Date(lastSynced), 'MM/dd/yyyy hh:mm a')}`;

type ArcgisLayerRowProps = {
  layer: ArcgisLayer;
};

const ArcgisLayerRow = ({ layer }: ArcgisLayerRowProps) => {
  const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [editing, setEditing] = useState<boolean>(false);

  const dispatch = useDispatch();

  const { layers, setFilter } = useMapLayers();
  const { geospatialLayers: enabled, arcgisLayerIds } = layers;
  const { refresh, remove, fetchingIds } = useArcgisLayers();

  const lastSynced = useSelector(
    (state: RootState) => state.arcgisLayerData[layer.id]?.lastSynced,
  );

  const canSync = hasRolePermissions(ROLE_SETS.INSPECTOR);
  const canManage = hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const fetching = fetchingIds.has(layer.id);

  const handleConfirmDelete = async () => {
    const { id, name } = layer;
    setDeleting(true);

    try {
      await remove(id);
      if (arcgisLayerIds.has(id)) {
        setFilter('arcgisLayerIds', toggleFromSet(arcgisLayerIds, id));
      }

      dispatch(removeArcgisLayerData(id));
      Snackbar.info({ message: `"${name}" removed` });
      setConfirmingDelete(false);
    } catch {
      Snackbar.error({
        message: `Unable to remove "${name}". Please try again.`,
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className={classes.layerRow}>
        <Checkbox
          edge="start"
          checked={arcgisLayerIds.has(layer.id)}
          onChange={() =>
            setFilter('arcgisLayerIds', toggleFromSet(arcgisLayerIds, layer.id))
          }
          disabled={!enabled}
          data-cy={`dashboardGeospatialFilter-${layer.name}`}
        />
        <span className={classes.arcgisRowIcon}>
          <ArcgisLayerIcon iconType={layer.iconType} colorId={layer.colorId} />
        </span>
        <span className={classes.arcgisRowText}>
          <span className={classes.layerName} title={layer.link}>
            {layer.name}
          </span>
          {lastSynced && (
            <span
              className={classes.lastSynced}
              data-cy={`dashboardGeospatialLastSynced-${layer.name}`}
            >
              {formatLastSynced(lastSynced)}
            </span>
          )}
        </span>
        {fetching ? (
          <span
            className={classes.rowSpinner}
            data-cy={`dashboardGeospatialLoading-${layer.name}`}
          >
            <Progress size={20} />
          </span>
        ) : (
          <span className={classes.rowActions}>
            {canSync && (
              <IconButton
                size="small"
                tooltip="Sync"
                onClick={() => refresh(layer)}
                data-cy={`dashboardGeospatialSync-${layer.name}`}
              >
                <Icon icon={RefreshIcon} />
              </IconButton>
            )}
            <IconButton
              size="small"
              tooltip={canManage ? 'Edit' : 'View Details'}
              onClick={() => setEditing(true)}
              data-cy={`dashboardGeospatialEdit-${layer.name}`}
            >
              <Icon icon={canManage ? EditIcon : InfoOutlinedIcon} />
            </IconButton>
            {canManage && (
              <IconButton
                size="small"
                tooltip="Remove"
                onClick={() => setConfirmingDelete(true)}
                data-cy={`dashboardGeospatialRemove-${layer.name}`}
              >
                <Icon icon={DeleteIcon} />
              </IconButton>
            )}
          </span>
        )}
      </div>
      <DeleteModal
        open={confirmingDelete}
        loading={deleting}
        disabled={deleting}
        title={`Remove Layer "${layer.name}"?`}
        content={`Are you sure you want to remove geospatial layer “${layer.name}” from the map?`}
        confirmText="Remove"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
      <GeospatialLayerModal
        open={editing}
        layer={layer}
        readOnly={!canManage}
        onClose={() => setEditing(false)}
      />
    </>
  );
};

export default ArcgisLayerRow;
